/**
 * Reference
 * 1. cleancode-api cora-core-api-documentation.yaml
 * 2. cleancode-api cleancode-api-documentation.yaml
 */

import { getQueryUrl } from 'utils';

const apiVersion = 'v1';

export const apiUrls = {
  // Cookie
  get_cookie: '/sanctum/csrf-cookie',

  // Organization
  get_organizations: `/${apiVersion}/organizations`,
  post_organizations: `/${apiVersion}/organizations`,
  get_organizations_id: (id: number) => `/${apiVersion}/organizations/${id}`,
  put_organizations_id: (id: number) => `/${apiVersion}/organizations/${id}`,
  put_organization: (organization_slug: string) => `/${organization_slug}/${apiVersion}/organizations`,
  post_organization_logo: (organization_slug: string) => `/${organization_slug}/${apiVersion}/organizations/logo`,
  delete_organizations_id: (id: number) => `/${apiVersion}/organizations/${id}`,
  post_organizations_invite: (organization_slug: string) => `/${organization_slug}/${apiVersion}/organizations/invite`,
  delete_organizations_id_delete_user_user_id: (id: number, user_id: number) =>
    `/${apiVersion}/organizations/${id}/delete-user/${user_id}`,
  post_organizations_register: `/${apiVersion}/organizations/register`,

  // Authentication
  post_login: `/${apiVersion}/login`,
  post_forgot_password: `/${apiVersion}/forgot-password`,
  post_reset_password: `/${apiVersion}/reset-password`,

  // Signup
  post_email_verification_notification: `/${apiVersion}/email/verification-notification`,

  // Billing
  get_stripe_intent: (organization_slug: string) => `/${organization_slug}/${apiVersion}/stripe-payment-intent`,
  post_stripe_payment: `/${apiVersion}/stripe-payment`,
  get_invoices: (organization_slug: string) => `/${organization_slug}/${apiVersion}/invoices`,
  get_payment_method: (organization_slug: string) => `/${organization_slug}/${apiVersion}/payment-methods`,
  create_payment_method: (organization_slug: string) => `/${organization_slug}/${apiVersion}/payment-methods`,
  preview_subscription: (organization_slug: string) => `/${organization_slug}/${apiVersion}/subscription-preview`,
  delete_zone_preview_subscription: (organization_slug: string) =>
    `/${organization_slug}/${apiVersion}/subscription-preview/delete-zone`,
  create_subscription: (organization_slug: string) => `/${organization_slug}/${apiVersion}/subscription`,
  update_subscription: (organization_slug: string) => `/${organization_slug}/${apiVersion}/subscription`,
  cancel_subscription: (organization_slug: string) => `/${organization_slug}/${apiVersion}/subscription`,
  put_billing_addresses: (organization_slug: string) =>
    `/${organization_slug}/${apiVersion}/payment-methods/billing-addresses`,

  // User
  get_user: `/${apiVersion}/user`,
  put_user: `/${apiVersion}/user`,
  post_user_profile_photo: `/${apiVersion}/user/profile-photo`,
  delete_user_profile_photo: `/${apiVersion}/user/profile-photo`,
  put_users_id: (organization_slug: string, id: number) => `/${organization_slug}/${apiVersion}/users/${id}`,
  get_users: (organization_slug: string) => `/${organization_slug}/${apiVersion}/users`,
  put_user_role: (organization_slug: string, id: number) => `/${organization_slug}/${apiVersion}/user/${id}/role`,
  get_user_assigned_buildings: (organization_slug: string, id: number) =>
    `${organization_slug}/${apiVersion}/user/${id}/building/assignment`,
  delete_user_assigned_building: (organization_slug: string, userId: number, buildingId: number) =>
    `${organization_slug}/${apiVersion}/user/${userId}/building/${buildingId}/assignment`,

  post_user_assigned_buildings: (organization_slug: string, id: number) =>
    `${organization_slug}/${apiVersion}/user/${id}/building/assignment`,

  // Buildings
  get_buildings: (organization_slug: string) => `/${organization_slug}/${apiVersion}/buildings`,
  post_buildings: (organization_slug: string) => `/${organization_slug}/${apiVersion}/buildings`,
  get_buildings_id: (organization_slug: string, id: number) => `/${organization_slug}/${apiVersion}/buildings/${id}`,
  post_buildings_id: (organization_slug: string, id: number) => `/${organization_slug}/${apiVersion}/buildings/${id}`,
  delete_buildings_id: (organization_slug: string, id: number) => `/${organization_slug}/${apiVersion}/buildings/${id}`,
  get_building_logs: ({
    organization_slug,
    buildingId,
    start,
    end,
  }: {
    organization_slug: string;
    buildingId: number;
    start: string;
    end: string;
  }) => `/${organization_slug}/${apiVersion}/building/${buildingId}/log?start=${start}&end=${end}`,
  get_building_feedbacks: ({
    organization_slug,
    buildingId,
    start,
    end,
  }: {
    organization_slug: string;
    buildingId: number;
    start: string;
    end: string;
  }) => `/${organization_slug}/${apiVersion}/building/${buildingId}/feedback?start=${start}&end=${end}`,

  // Zone
  get_zones: (organization_slug: string) => `/${organization_slug}/${apiVersion}/zone?rating=true&last_serviced=true`,
  post_zone: (organization_slug: string) => `/${organization_slug}/${apiVersion}/zone`,
  put_zone: (organization_slug: string, zoneId: number) => `/${organization_slug}/${apiVersion}/zone/${zoneId}`,

  // zone specific endpoints
  get_zone: (organization_slug: string, id: number) => `/${organization_slug}/${apiVersion}/zone/${id}`,
  delete_zone: (organization_slug: string, id: number) => `/${organization_slug}/${apiVersion}/zone/${id}`,
  inactivate_zone: (organization_slug: string, id: number) => `/${organization_slug}/${apiVersion}/zone/${id}/inactive`,
  put_pending_zone: (organization_slug: string, id: number) => `/${organization_slug}/${apiVersion}/zone/${id}/pending`,

  // zone service items
  get_zone_id_service_items: (organization_slug: string, zoneId: number) =>
    `/${organization_slug}/${apiVersion}/zone/${zoneId}/service-item`,
  post_zone_id_service_items: (organization_slug: string, zoneId: number) =>
    `/${organization_slug}/${apiVersion}/zone/${zoneId}/service-item`,
  put_zone_zoneId_service_item_id: (organization_slug: string, zoneId: number, id: number) =>
    `/${organization_slug}/${apiVersion}/zone/${zoneId}/service-item/${id}`,
  delete_zone_zoneId_service_item_id: (organization_slug: string, zoneId: number, id: number) =>
    `/${organization_slug}/${apiVersion}/zone/${zoneId}/service-item/${id}`,

  // Decals
  get_decal: (organization_slug: string, zoneId: number) => `/${organization_slug}/${apiVersion}/decals/${zoneId}`,
  download_decal: (organization_slug: string, id: number | undefined) =>
    `/${organization_slug}/${apiVersion}/decals/${id}`,
  approve_decal: (organization_slug: string, id: number) => `/${organization_slug}/${apiVersion}/decals/${id}/approve`,
  unapprove_decal: (organization_slug: string, id: number) =>
    `/${organization_slug}/${apiVersion}/decals/${id}/unapprove`,

  // Feedback
  get_feedback: (organization_slug: string) => `/${organization_slug}/${apiVersion}/feedback`,

  // search
  get_search: (organization_slug: string) => `/${organization_slug}/${apiVersion}/search`,

  // report
  get_report: (organization_slug: string, buildingId: number) =>
    `/${organization_slug}/${apiVersion}/report/${buildingId}`,

  // Health Check
  get_health_check: '/health-check',

  // Decal
  get_zone_id_decal: (id: number) => `/zone/${id}/decal`,

  // Employee
  get_employees: (organization_slug: string) => `/${organization_slug}/${apiVersion}/employee`,
  get_employee: (organization_slug: string) => `${organization_slug}/${apiVersion}/employee`,
  post_employee: (organization_slug: string) => `${organization_slug}/${apiVersion}/employee`,
  get_employee_id: (organization_slug: string, id: number) => `${organization_slug}/${apiVersion}/employee/${id}`,
  put_employee_id: (organization_slug: string, id: number) => `${organization_slug}/${apiVersion}/employee/${id}`,
  delete_employee_id: (organization_slug: string, id: number) => `${organization_slug}/${apiVersion}/employee/${id}`,
  get_employee_code: (organization_slug: string) => `${organization_slug}/${apiVersion}/employee/code`,

  // Feedback
  post_zone_zoneId_contact_form: (zoneId: number) => `/zone/${zoneId}/contact-form`,
  post_zone_zoneId_feedback: (zoneId: number) => `/zone/${zoneId}/feedback`,

  // Metrics
  get_metrics_rating_average: (organization_slug: string) =>
    `${organization_slug}/${apiVersion}/metrics/rating/average`,
  get_metrics_contact_form_submission_count: (organization_slug: string) =>
    `${organization_slug}/${apiVersion}/metrics/contact-form/submission/count`,
  get_metrics_scans_count: (payload: {
    organization_slug: string;
    queryObj: {
      buildingId?: number;
      zoneId?: number;
      start: string;
      end: string;
    };
  }) => {
    const { organization_slug, queryObj } = payload;

    return getQueryUrl({
      url: `${organization_slug}/${apiVersion}/metrics/scans/count`,
      queryObj,
    });
  },
  get_metrics_subscriber_count: (payload: {
    organization_slug: string;
    queryObj: {
      buildingId?: number;
      zoneId?: number;
    };
  }) => {
    const { organization_slug, queryObj } = payload;

    return getQueryUrl({
      url: `${organization_slug}/${apiVersion}/metrics/subscriber/count`,
      queryObj,
    });
  },
  get_metrics_schedule_progress: (payload: {
    organization_slug: string;
    queryObj: {
      buildingId?: number;
      zoneId?: number;
      date?: string;
    };
  }) => {
    const { organization_slug, queryObj } = payload;

    return getQueryUrl({
      url: `${organization_slug}/${apiVersion}/metrics/schedule/progress`,
      queryObj,
    });
  },

  // Subscribe
  post_zone_zoneId_subscribe: (zoneId: number) => `/zone/${zoneId}/subscribe`,

  // Zone specific
  get_zone_code_code: (code: string) => `/zone/code/${code}`,
  put_zone_zoneId: (zoneId: number) => `/zone/${zoneId}`,
  delete_zone_zoneId: (zoneId: number) => `/zone/${zoneId}`,
  get_zone_expired: '/zone/expired',
  put_zone_zoneId_disable: (zoneId: number) => `/zone/${zoneId}/disable`,

  // Zone Logs
  get_zone_zoneId_log: (zoneId: number) => `/${apiVersion}/zone/${zoneId}/log`,
  get_zone_zoneId_cleaning_history: ({ zoneId, date }: { zoneId: number; date: string }) =>
    `/${apiVersion}/zone/${zoneId}/cleaning-history?date=${date}`, // ?date=2022-05-12
  post_zone_zoneId_log: (zoneId: number) => `/${apiVersion}/zone/${zoneId}/log`,
};
